import {gql, MutationUpdaterFn, useMutation} from '@apollo/client'
import {cloneDeep} from '@apollo/client/utilities'
import {from} from 'fromfrom'
import produce from 'immer'
import {useAlert} from 'react-alert'
import {CurrentOrganisation} from './CurrentOrganisationContext'
import {useOrganisationIdStr} from './useOrganisationIdStr'
import {useQueue} from './useQueue'
import {ApprovePhotos, ApprovePhotosVariables} from './__types__/ApprovePhotos'
import {SendPhotoCollectionEmails, SendPhotoCollectionEmailsVariables} from './__types__/SendPhotoCollectionEmails'
import {PHOTOS_DATA, UploadPhotoMutationFn, useLinkPhoto} from '~/apollo/queries/Photos'
import {PhotosData, PhotosData_organisation_photos, PhotosDataVariables} from '~/apollo/queries/__types__/PhotosData'

function searchProfiles<ProfileType extends {fullName: string}>(profiles: ProfileType[], searchTerm: string) {
    return from(profiles)
        .filter((profile) => profile.fullName.toLowerCase().includes(searchTerm.toLocaleLowerCase()))
        .sortBy((profile) => profile.fullName.toLowerCase())
        .toArray()
}

function searchPhotos<
    PhotoType extends {
        filename: string
        profile?: {fullName: string} | null
        pendingProfile?: {fullName: string} | null
    }
>(photos: PhotoType[], searchTerm: string) {
    return photos.filter((photo) => {
        if (photo.profile) return photo.profile.fullName.toLowerCase().includes(searchTerm.toLowerCase())
        if (photo.pendingProfile) return photo.pendingProfile.fullName.toLowerCase().includes(searchTerm.toLowerCase())

        return photo.filename.toLowerCase().includes(searchTerm.toLowerCase())
    })
}

function sortProfiles<ProfileType extends {fullName: string}>(profiles: ProfileType[]) {
    return from(profiles)
        .sortBy((profile) => profile.fullName.toLowerCase())
        .toArray()
}

function sortPhotos<PhotoType extends {filename: string; profile?: null | {fullName: string}}>(photos: PhotoType[]) {
    return (
        from(photos)
            // Unmatched first
            .sortBy((photo) => (photo.profile ? 1 : 0))
            // Sort by profile name if matched, or photo name
            .sortBy((photo) => {
                if (photo.profile) return photo.profile.fullName.toLowerCase()
                return photo.filename.toLowerCase()
            })
            .toArray()
    )
}

const APPROVE_PHOTOS = gql`
    mutation ApprovePhotos($photoIdStrs: [String!]!) {
        approvePhotos(photoIdStrs: $photoIdStrs) {
            photos {
                idStr
                profile {
                    idStr
                }
                pendingProfile {
                    idStr
                }
            }
            profiles {
                idStr
                photos {
                    idStr
                }
            }
        }
    }
`

const useApprovePhotos = ({photoIdStrs}: {photoIdStrs: string[]}) => {
    const {approvePhotosUpdate: update} = useApprovePhotosUpdater()
    return useMutation<ApprovePhotos, ApprovePhotosVariables>(APPROVE_PHOTOS, {
        variables: {photoIdStrs},
        update,
    })
}

const useApprovePhotosUpdater = () => {
    const {currentOrganisation} = CurrentOrganisation.useContainer()

    const approvePhotosUpdate: MutationUpdaterFn<ApprovePhotos> = (cache, {data}) => {
        if (!currentOrganisation) return
        if (!data || !data.approvePhotos || !data.approvePhotos.photos || !data.approvePhotos.profiles) return

        const approvedPhotos = data.approvePhotos.photos.map((photo) => photo.idStr)
        const approvedProfiles = data.approvePhotos.profiles.map((profile) => profile.idStr)

        const deletedPhoto = (photo: PhotosData_organisation_photos) =>
            photo.profile && !approvedPhotos.includes(photo.idStr) && approvedProfiles.includes(photo.profile.idStr)

        const organisationIdStr = currentOrganisation.idStr

        const getPhotosResult = cache.readQuery<PhotosData, PhotosDataVariables>({
            query: PHOTOS_DATA,
            variables: {organisationIdStr},
        })
        if (!getPhotosResult) return

        cache.writeQuery<PhotosData, PhotosDataVariables>({
            query: PHOTOS_DATA,
            data: produce(cloneDeep(getPhotosResult), (draftState) => {
                draftState.organisation.photos = draftState.organisation.photos.filter(
                    (photo) => approvedPhotos.includes(photo.idStr) || !deletedPhoto(photo),
                )
            }),
            variables: {organisationIdStr},
        })
    }

    return {approvePhotosUpdate}
}

type FileWithProfileId = {
    file: File
    profileIdStr: string
}

type PhotoQueueType = FileWithProfileId | File

export function isFile(queueItem: PhotoQueueType): queueItem is File {
    return queueItem instanceof File
}

const useUploadPhotoQueue = (uploadPhoto: UploadPhotoMutationFn) => {
    const organisationIdStr = useOrganisationIdStr()
    const [linkPhoto] = useLinkPhoto()
    const alert = useAlert()

    const queueState = useQueue<PhotoQueueType>(async (photoQueueItem) => {
        const file = photoQueueItem instanceof File ? photoQueueItem : photoQueueItem.file
        const profileIdStr = photoQueueItem instanceof File ? null : photoQueueItem.profileIdStr

        try {
            const response = await uploadPhoto({
                variables: {
                    organisationIdStr,
                    imgFile: file,
                },
            })
            if (profileIdStr && response && response.data && response.data.uploadPhoto) {
                await linkPhoto({
                    variables: {
                        photoIdStr: response.data.uploadPhoto.idStr,
                        profileIdStr,
                    },
                })
            }
        } catch (error) {
            alert.error(`Failed to upload ${file.name}`)
        }
    })

    const profilesLoading: string[] = []

    for (const photoQueueItem of [...queueState.pending, ...queueState.current]) {
        if (!(photoQueueItem instanceof File)) {
            profilesLoading.push(photoQueueItem.profileIdStr)
        }
    }

    return {...queueState, profilesLoading}
}

export const SEND_PHOTO_COLLECTION_EMAIL = gql`
    mutation SendPhotoCollectionEmails($profileIdStrs: [String!]!, $organisationIdStr: String!) {
        sendPhotoCollectionEmails(profileIdStrs: $profileIdStrs, organisationIdStr: $organisationIdStr) {
            idStr
            photoRequested
        }
    }
`

const useSendPhotoCollectionEmails = () =>
    useMutation<SendPhotoCollectionEmails, SendPhotoCollectionEmailsVariables>(SEND_PHOTO_COLLECTION_EMAIL)

export {
    searchProfiles,
    useApprovePhotos,
    searchPhotos,
    sortProfiles,
    sortPhotos,
    useUploadPhotoQueue,
    useSendPhotoCollectionEmails,
}
